import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    calculatePriceService,
    clearTimer,
    forgetPasswordService,
    loginAsService,
    loginService,
    logoutService,
    newCustomerEligibleService,
    newCustomerService,
    payAsGuestService,
    selfRegisterService,
    startSessionTimer,
    verifyEmailService
} from '../../services/loginService';

import { ForgetArgs, LoginArgs, LoginAsArgs, LoginState, UserInfo } from 'MyAccountPlus-UIComponents';
import { CalCulateOrderPrice, NewCustomerArgs, NewCustomerEligible, NewCustomerEligibleArgs, PayAsGuestArgs, SelfRegisterArgs } from './types';
import { paymentActions } from '../../store/user-reducers/payment-reducer/paymentSlice';
import { userAccountActions } from '../../store/user-reducers/user-account-reducer/userAccountSlice';

const localeUserKey = sessionStorage.userInfo && JSON.parse(sessionStorage.userInfo).user_key

interface VerifyEmail {
    buttonLabel: string;
    data: UserInfo;
}

interface LoginInitialState extends LoginState, NewCustomerEligible, VerifyEmail {
    orderPrice: {
        total_price: string;
    };
}

const initialState: LoginInitialState = {
    user_key: localeUserKey?.length ? localeUserKey : "",
    loading: false,
    error: "",
    userInfo: {
        level: '',
        locale: '',
        role: '',
        tenant: {
            root: false,
            tenant: {
                reseller: false,
                since: 0,
                uri: ''
            }
        },
        user_id: '',
        user_key: '',
        display_name: ''
    },
    newCustomerEligible: {
        is_eligible: false,
        eligibility_message: ''
    },
    success: "",
    buttonLabel: "",
    data: {
        level: '',
        locale: '',
        role: '',
        tenant: {
            root: false,
            tenant: {
                reseller: false,
                since: 0,
                uri: ''
            }
        },
        user_id: '',
        user_key: '',
        display_name: ''
    },
    orderPrice: {
        total_price: ''
    }
}

export const loginThunk = createAsyncThunk("login/loginThunk", async (arg: LoginArgs, thunkAPI) => {
    try {
        const payload = {
            user_id: arg.user_id,
            password: arg.password,
        }
        const { data } = await loginService(payload)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const forgetPasswordThunk = createAsyncThunk("login/forgetPasswordThunk", async (arg: ForgetArgs, thunkAPI) => {
    try {
        const { data } = await forgetPasswordService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const loginAsThunk = createAsyncThunk("login/loginAsThunk", async (arg: LoginAsArgs, thunkAPI) => {
    try {
        const { data } = await loginAsService(arg.user_id)
        // Store current path and admin info
        localStorage.setItem("last_route", window.location.pathname);
        const currentUserInfo = sessionStorage.getItem("userInfo");
        if (currentUserInfo) {
            sessionStorage.setItem("adminInfo", currentUserInfo);
        }
        // Clear all relevant user state before setting new user data
        thunkAPI.dispatch(paymentActions.clearState());
        thunkAPI.dispatch(userAccountActions.clearState());
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

export const newCustomerEligibleThunk = createAsyncThunk("login/newCustomerEligibleThunk", async (arg: NewCustomerEligibleArgs, thunkAPI) => {
    try {
        const { data } = await newCustomerEligibleService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error);
    }
});

export const newCustomerThunk = createAsyncThunk("login/newCustomerThunk", async (arg: NewCustomerArgs, thunkAPI) => {
    try {
        const { data } = await newCustomerService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error.error_code);
    }
});

export const selfRegisterThunk = createAsyncThunk("login/selfRegisterThunk", async (arg: SelfRegisterArgs, thunkAPI) => {
    try {
        const { data } = await selfRegisterService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error.error_code);
    }
});

export const payAsGuestThunk = createAsyncThunk("login/payAsGuestThunk", async (arg: PayAsGuestArgs, thunkAPI) => {
    try {
        const { data } = await payAsGuestService(arg)
        return { data }
    } catch (error: any) {
        console.log("error--", error.response.data);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error.error_code);
    }
});

export const verifyUserEmailThunk = createAsyncThunk("login/verifyUserEmailThunk", async (args: string, thunkAPI) => {
    try {
        const { data } = await verifyEmailService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue('(' + error.response.status + ') ' + (error.response.status === 401 ? "Unauthorized" : error.response.data.detail || "Unauthorized"));
    }
});

export const calculateOrderPriceThunk = createAsyncThunk("login/calculateOrderPriceThunk", async (args: CalCulateOrderPrice, thunkAPI) => {
    try {
        const { data } = await calculatePriceService(args);
        return { data };
    } catch (error: any) {
        console.log("error--", error.response);
        if (!error.response) {
            throw error
        }
        return thunkAPI.rejectWithValue(error.response.data?.error.error_code);
    }
});

export const loginSlice = createSlice({
    name: 'login',
    initialState,
    reducers: {
        errorReducer: (state) => {
            state.error = ""
        },
        logoutReducer: (state) => {
            state.user_key = ""
            state.loading = false
            state.success = ""
            state.error = ""
            state.userInfo = {
                level: '',
                locale: '',
                role: '',
                tenant: {
                    root: false,
                    tenant: {
                        reseller: false,
                        since: 0,
                        uri: ''
                    }
                },
                display_name: '',
                user_id: '',
                user_key: ''
            }
            clearTimer();
            logoutService();
            // Preserve adminInfo if it exists
            const adminInfo = sessionStorage.getItem("adminInfo");
            sessionStorage.clear();
            if (adminInfo) {
                sessionStorage.setItem("adminInfo", adminInfo);
            }
        },
        successReducer: (state) => {
            state.success = "";
            state.buttonLabel = "";
        },
        eligibleReducer: (state) => {
            state.newCustomerEligible = initialState.newCustomerEligible
        },
        viewMyAccountReducer: (state) => {
            state.userInfo = state.data
            state.user_key = state.data.user_key
            sessionStorage.setItem("userInfo", JSON.stringify(state.data));
            startSessionTimer();
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loginThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(loginThunk.fulfilled, (state, action) => {
                state.userInfo = action.payload.data
                state.user_key = action.payload.data.user_key
                state.loading = false
                state.error = ""
                sessionStorage.setItem("userInfo", JSON.stringify(action.payload.data));
                startSessionTimer()
            })
            .addCase(loginThunk.rejected, (state, action: any) => {
                state.error = action.payload?.detail || action.error.message!
                state.loading = false
            })
            .addCase(forgetPasswordThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(forgetPasswordThunk.fulfilled, (state, action) => {
                state.success = "Email sent successfully!"
                state.loading = false
                state.error = ""
            })
            .addCase(forgetPasswordThunk.rejected, (state, action: any) => {
                state.error = action.payload.detail || action.error.message!
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(loginAsThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(loginAsThunk.fulfilled, (state, action) => {
                state.userInfo = action.payload.data
                state.user_key = action.payload.data.user_key
                state.loading = false
                state.error = ""
                sessionStorage.setItem("userInfo", JSON.stringify(action.payload.data));
                startSessionTimer()
            })
            .addCase(loginAsThunk.rejected, (state, action: any) => {
                state.error = action.payload.detail || action.error.message!
                state.loading = false
            })
            .addCase(newCustomerEligibleThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(newCustomerEligibleThunk.fulfilled, (state, action) => {
                state.newCustomerEligible = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(newCustomerEligibleThunk.rejected, (state, action) => {
                state.error = state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(newCustomerThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(newCustomerThunk.fulfilled, (state, action) => {
                state.success = "New customer created successfully"
                state.newCustomerEligible = initialState.newCustomerEligible
                state.loading = false
                state.error = ""
            })
            .addCase(newCustomerThunk.rejected, (state, action) => {
                state.error = state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(selfRegisterThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(selfRegisterThunk.fulfilled, (state, action) => {
                state.success = "User registered successfully"
                state.loading = false
                state.error = ""
            })
            .addCase(selfRegisterThunk.rejected, (state, action) => {
                state.error = state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(payAsGuestThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(payAsGuestThunk.fulfilled, (state, action) => {
                state.loading = false
                action.payload.data.success ?
                    state.success = action.payload.data.message :
                    state.error = action.payload.data.message
            })
            .addCase(payAsGuestThunk.rejected, (state, action) => {
                console.log("action error--", action.error, action.payload)
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(verifyUserEmailThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(verifyUserEmailThunk.fulfilled, (state, action) => {
                state.success = action.payload.data.welcome_message;
                state.buttonLabel = action.payload.data?.button_label || "";
                state.data = action.payload.data;
                state.loading = false
                state.error = ""
            })
            .addCase(verifyUserEmailThunk.rejected, (state, action) => {
                state.error = action.payload as string || action.error.message || "";
                state.loading = false
            })
            .addCase(calculateOrderPriceThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(calculateOrderPriceThunk.fulfilled, (state, action) => {
                state.orderPrice = action.payload.data;
                state.loading = false
                state.error = ""
            })
            .addCase(calculateOrderPriceThunk.rejected, (state, action) => {
                state.error = action.error.message || action.payload as string || "";
                state.loading = false
            })
    },
})

export const loginActions = loginSlice.actions
export default loginSlice.reducer;